import React, { useEffect, useState, useCallback, useMemo, useRef } from "react"
import { Link } from 'react-router-dom'
import Flag from 'react-flagkit'
import { useDispatch } from 'react-redux'
import { fetchLoginUrl } from '../redux/slices/loginSlice'
import Layout from '../layout/Layout'
import Subheader from '../components/Subheader'
import Button from '../components/Button'
import Footer from '../components/Footer'
import CommonModal from '../components/CommonModal'
import CloseButton from '../components/CloseButton'
import styles from './WholesaleWizard.module.css'
import ProductListUpload from '../components/ProductListUpload'
import { useWholesaleAllFavoriteQuery, useWholesaleAllFilesQuery, useDeleteFavoriteMutation, useDeleteWholesaleListMutation, useWholesaleSettingsQuery, useUpdateWholesaleSettingsMutation } from '../services/wholesale'
import ProgressCircle from '../components/ProgressCircle'
import { ReactComponent as CogIcon } from '../icons/cog.svg'
import useLocalStorageState from 'use-local-storage-state'
import notificationStyles from '../components/NotificationBar.module.css'
import Tippy from '@tippyjs/react'

const AVAILABLE_REGIONS = [
  { code: 'de', label: 'Germany', flag: 'DE' },
  { code: 'fr', label: 'France', flag: 'FR' },
  { code: 'it', label: 'Italy', flag: 'IT' },
  { code: 'es', label: 'Spain', flag: 'ES' },
]

export default function WholesaleWizard() {
  let dispatch = useDispatch()
  let [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  let [isSettingsOpen, setIsSettingsOpen] = useState(false)
  let [shippingMode, setShippingMode] = useLocalStorageState('wholesale-shipping-mode', {
    defaultValue: 'paneu'
  })
  let [efnRegion, setEfnRegion] = useLocalStorageState('wholesale-efn-region', {
    defaultValue: []
  })
  let [fbmShippingCost, setFbmShippingCost] = useLocalStorageState('wholesale-fbm-shipping-cost', {
    defaultValue: ''
  })
  let { data: settings } = useWholesaleSettingsQuery()
  let { mutate: saveSettings, isPending: isSavingSettings } = useUpdateWholesaleSettingsMutation()

  useEffect(() => {
    dispatch(fetchLoginUrl())
  }, [dispatch])

  let { data: lists, isFetching: isListsLoading, refetch: refetchLists } = useWholesaleAllFilesQuery()
  let { data: allFavorite, isFetching: isFavoriteLoading, refetch: refetchFavorites } = useWholesaleAllFavoriteQuery()
  useEffect(() => {
    if (!settings) return
    let ft = settings.fullfillment_type
    let mode = ft === 'efn' ? 'efn' : ft === 'fbm' ? 'fbm' : 'paneu'
    let marketplace = settings.marketplace ? String(settings.marketplace).toLowerCase() : null

    setShippingMode(mode)
    if (mode === 'efn' && marketplace) {
      setEfnRegion([marketplace])
    } else if (mode !== 'efn') {
      setEfnRegion([])
    }
    if (mode === 'fbm' && settings.fbm_shipping_cost != null) {
      setFbmShippingCost(String(settings.fbm_shipping_cost))
    }
  }, [settings, setShippingMode, setEfnRegion, setFbmShippingCost])

  let handleSettingsUpdate = useCallback((mode, regions, shippingCost) => {
    if (mode === 'efn' && !(regions?.[0])) {
      return
    }
    if (mode === 'fbm') {
      let cost = parseFloat(shippingCost)
      if (isNaN(cost) || cost < 0) return
    }
    let marketplace = mode === 'efn' ? regions?.[0] : null
    let payload = { fullfillment_type: mode }
    if (marketplace) {
      payload.marketplace = marketplace
    }
    if (mode === 'fbm') {
      payload.fbm_shipping_cost = parseFloat(shippingCost)
    }
    saveSettings(payload)
  }, [saveSettings])

  let handleEfnRegionChange = useCallback((regions) => {
    setEfnRegion(regions)
    if (shippingMode === 'efn' && regions?.[0]) {
      handleSettingsUpdate('efn', regions)
    }
  }, [shippingMode, handleSettingsUpdate, setEfnRegion])

  let handleRegionSelect = useCallback((regionCode) => {
    if (!regionCode || isSavingSettings) return
    setEfnRegion([regionCode])
    handleSettingsUpdate('efn', [regionCode])
  }, [handleSettingsUpdate, isSavingSettings, setEfnRegion])

  let handleFbmShippingCostChange = useCallback((e) => {
    setFbmShippingCost(e.target.value)
  }, [setFbmShippingCost])

  let handleFbmShippingCostBlur = useCallback(() => {
    if (shippingMode !== 'fbm') return
    let cost = parseFloat(fbmShippingCost)
    if (isNaN(cost) || cost < 0 || cost > 500) return
    handleSettingsUpdate('fbm', [], fbmShippingCost)
  }, [shippingMode, handleSettingsUpdate, fbmShippingCost])

  useEffect(() => {
    if (!lists) return
    let hasPending = lists.some(item => !item.available)
    if (!hasPending) return
    let interval = setInterval(() => {
      refetchLists()
      refetchFavorites()
    }, 3000)
    return () => clearInterval(interval)
  }, [lists, refetchLists, refetchFavorites])

  // Aggregated statistics for the dashboard card
  const stats = useMemo(() => {
    if (!lists || !Array.isArray(lists)) {
      return {
        scannedCount: 0,
        totalLists: 0,
        totalAsins: 0,
        topMarketCode: null,
        topMarketCount: 0,
      }
    }

    const scannedCount = lists.filter((l) => l?.available).length
    const totalLists = lists.length
    const totalAsins = lists.reduce((sum, l) => sum + (l?.asin_count || 0), 0)

    const marketCounts = lists.reduce((acc, l) => {
      const code = (l?.marketplace || l?.region || '').toUpperCase()
      if (!code) return acc
      acc[code] = (acc[code] || 0) + 1
      return acc
    }, {})

    const [topMarketCode, topMarketCount] = Object.entries(marketCounts)
      .sort((a, b) => b[1] - a[1])[0] || [null, 0]

    return { scannedCount, totalLists, totalAsins, topMarketCode, topMarketCount }
  }, [lists])

  return (
    <Layout>
      <CommonModal
        isOpen={isUploadModalOpen}
        closeModal={() => setIsUploadModalOpen(false)}
      >
        <div className='relative'>
          <CloseButton onClick={() => setIsUploadModalOpen(false)} className='absolute top-4 right-4 z-10' />
          <ProductListUpload onClose={() => setIsUploadModalOpen(false)} />
        </div>
      </CommonModal>
      <section className={`${styles.layout} bg-background`}>
        <header className="flex flex-row px-4 py-2 items-center pb-0">
          <div className='flex-1'>
            <Subheader date>
              Wholesale <em>Wizard</em>
            </Subheader>
          </div>
          <div className='flex items-center gap-2'>
            <Tippy
              visible={isSettingsOpen}
              onClickOutside={() => setIsSettingsOpen(false)}
              interactive
              placement='bottom-end'
              arrow={false}
              className={notificationStyles.tooltip}
              content={
                <div className={notificationStyles.dropdownContent}>
                  <div className='flex flex-col gap-2'>
                    <button
                      type='button'
                      className={`${notificationStyles.toggleButton} ${shippingMode === 'paneu' ? notificationStyles.toggleButtonActive : ''} !px-3 !py-2 text-xs`}
                      disabled={isSavingSettings}
                      onClick={() => {
                        setShippingMode('paneu')
                        setEfnRegion([])
                        handleSettingsUpdate('paneu', [])
                      }}
                    >
                      <div className={notificationStyles.toggleIndicator} />
                      <span className='text-sm font-semibold'>PanEU</span>
                    </button>

                    <button
                      type='button'
                      className={`${notificationStyles.toggleButton} ${shippingMode === 'efn' ? notificationStyles.toggleButtonActive : ''} !px-3 !py-2 text-xs`}
                      disabled={isSavingSettings}
                      onClick={() => {
                        setShippingMode('efn')
                        if (!efnRegion?.length) {
                          setEfnRegion([])
                        }
                        handleSettingsUpdate('efn', efnRegion || [])
                      }}
                    >
                      <div className={notificationStyles.toggleIndicator} />
                      <span className='text-sm font-semibold'>EFN</span>
                    </button>

                    <button
                      type='button'
                      className={`${notificationStyles.toggleButton} ${shippingMode === 'fbm' ? notificationStyles.toggleButtonActive : ''} !px-3 !py-2 text-xs`}
                      disabled={isSavingSettings}
                      onClick={() => {
                        setShippingMode('fbm')
                        setEfnRegion([])
                        handleSettingsUpdate('fbm', [], fbmShippingCost)
                      }}
                    >
                      <div className={notificationStyles.toggleIndicator} />
                      <span className='text-sm font-semibold'>FBM</span>
                    </button>
                  </div>

                  {shippingMode === 'fbm' && (
                    <section>
                      <header className='mb-2 text-xs font-semibold text-neutral-500'>Versandkosten (€)</header>
                      <input
                        type='number'
                        min='0'
                        max='500'
                        step='0.01'
                        value={fbmShippingCost}
                        onChange={handleFbmShippingCostChange}
                        onBlur={handleFbmShippingCostBlur}
                        onKeyDown={(e) => { if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault() }}
                        disabled={isSavingSettings}
                        placeholder='z.B. 3.99'
                        className={`w-full rounded border px-2 py-1 text-sm focus:outline-none bg-transparent ${
                          fbmShippingCost && (parseFloat(fbmShippingCost) < 0 || parseFloat(fbmShippingCost) > 500)
                            ? 'border-red-400 dark:border-red-500'
                            : 'border-neutral-300 dark:border-neutral-600 focus:border-primary'
                        }`}
                      />
                      {(!fbmShippingCost || isNaN(parseFloat(fbmShippingCost))) && (
                        <p className={notificationStyles.helperText}>Bitte Versandkosten eingeben (0 – 500 €).</p>
                      )}
                      {fbmShippingCost && !isNaN(parseFloat(fbmShippingCost)) && (parseFloat(fbmShippingCost) < 0 || parseFloat(fbmShippingCost) > 500) && (
                        <p className='text-xs text-red-500 mt-1'>Nur Werte zwischen 0 und 500 € erlaubt.</p>
                      )}
                    </section>
                  )}

                  {shippingMode === 'efn' && (
                    <section>
                      <header className='mb-2 text-xs font-semibold text-neutral-500'>Märkte</header>
                      <div className={notificationStyles.marketFlags}>
                        {AVAILABLE_REGIONS.map((region) => {
                          let isActive = efnRegion?.[0] === region.code
                          return (
                            <button
                              key={region.code}
                              type='button'
                              onClick={() => handleRegionSelect(region.code)}
                              className={`${notificationStyles.flagButton} ${isActive ? notificationStyles.flagButtonActive : ''}`}
                              disabled={isSavingSettings}
                              aria-label={region.label}
                            >
                              <Flag country={region.flag} size={24} aria-label={region.label} />
                            </button>
                          )
                        })}
                      </div>
                      {!efnRegion?.length && (
                        <p className={notificationStyles.helperText}>Bitte wähle eine Region für EFN.</p>
                      )}
                    </section>
                  )}
                </div>
              }
            >
              <Button
                variant='primary'
                className='h-10 px-4'
                onClick={() => setIsSettingsOpen((prev) => !prev)}
              >
                <CogIcon className='h-4 w-auto dark:text-black' />
              </Button>
            </Tippy>
            <Button variant='primary' className='h-10 px-4' onClick={() => setIsUploadModalOpen(true)}>+ Hinzufügen</Button>
          </div>
        </header>
        <div className={styles.content}>
          <div className={styles.grid}>
            <div className={`${styles.card} ${styles.cardAllowOverflow}`}>
              <Table lists={lists} />
            </div>
            <div className={`${styles.card} ${styles.cardAllowOverflow}`}>
              <Table lists={allFavorite} />
            </div>

            {/* eany-Kachel vorerst ausgeblendet (nicht gelistete Funktion).
                Die Supplier-Ansicht bleibt direkt erreichbar unter
                /wholesale/supplier/eany. Zum Wieder-Einblenden Block reaktivieren. */}
            {/*
            <div className={`${styles.card} flex flex-col items-start gap-3 p-4`}>
              <img
                src='/static/eany-logo.png'
                alt='eany'
                className='h-[30px] w-auto'
                onError={(e) => { e.currentTarget.style.display = 'none' }}
              />
              <p className='subtitle2 text-neutral-500 dark:text-neutral-300'>
                <strong>eany</strong> – Großhandelskatalog mit über 800.000 Produkten,
                bereits ASIN-gematcht und mit Keepa-Daten angereichert. Stöbere durch
                die Deals wie in einer eigenen Liste.
              </p>
              <div className='flex gap-2'>
                <Link
                  to='/wholesale/supplier/eany'
                  className='btn-doc bg-primary text-white font-semibold text-sm flex items-center justify-center'
                >
                  Deals ansehen
                </Link>
              </div>
            </div>
            */}

            <div className={`${styles.card} ${styles.qogita}`}>
              <img src='/static/qogita-logo.png' alt='Qogita' className={styles.logo} />
              <p className='subtitle2 text-neutral-500 dark:text-neutral-300'>
                Qogita: Der B2B-Großhandelsmarktplatz, der Händler, Marken und Distributoren auf einer leistungsstarken Plattform verbindet.Source aus 500k Produkten. Mit dem Code EDGE sparst du 100€ auf den ersten Kauf!
              </p>

              <div className='flex gap-2'>
                <a
                  href="https://www.qogita.com/voucher/EDGE/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-doc bg-[#6E00FF] text-white font-semibold text-sm flex items-center justify-center"
                >
                  Sign up
                </a>
                <button
                  className="btn-doc bg-[#6E00FF1A] text-[#6E00FF] font-semibold text-sm border border-[#6E00FF] border-dashed"
                >
                  SAVE 100€
                </button>
              </div>
            </div>

            <div className={`${styles.card} ${styles.text}`}>
              <h5 className='title font-semibold mb-3'>
                Deine <span className='text-primary'>Statistiken</span>
              </h5>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-4 items-center'>
                <div className='flex flex-col'>
                  <span className='text-xs text-neutral-500'>Listen gescannt</span>
                  <span className='text-2xl font-bold'>
                    {stats.scannedCount}
                  </span>
                  <span className='text-xs text-neutral-400'>von {stats.totalLists} hochgeladen</span>
                </div>
                <div className='flex flex-col'>
                  <span className='text-xs text-neutral-500'>ASINs insgesamt</span>
                  <span className='text-2xl font-bold'>
                    {stats.totalAsins.toLocaleString?.() || stats.totalAsins}
                  </span>
                </div>
                <div className='flex items-center gap-3'>
                  <div className='flex items-center justify-center w-8 h-8 rounded-full bg-neutral-100 dark:bg-neutral-900'>
                    {stats.topMarketCode ? (
                      <Flag country={stats.topMarketCode} />
                    ) : (
                      <span className='text-neutral-400'>–</span>
                    )}
                  </div>
                  <div className='flex flex-col'>
                    <span className='text-xs text-neutral-500'>Häufigster Markt</span>
                    <span className='text-2xl font-bold'>
                      {stats.topMarketCode || '–'}
                    </span>
                    {stats.topMarketCode && (
                      <span className='text-xs text-neutral-400'>{stats.topMarketCount} {stats.topMarketCount === 1 ? 'Liste' : 'Listen'}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </section>
    </Layout>
  )
}


const PAGE_SIZE = 10

function Table({ lists }) {
  let [currentPage, setCurrentPage] = useState(1)
  let [hoveredListId, setHoveredListId] = useState(null)
  let hoverTimeoutRef = useRef(null)
  let handleBadgeEnter = useCallback((listId) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    setHoveredListId(listId)
  }, [])
  let handleBadgeLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredListId(null)
      hoverTimeoutRef.current = null
    }, 150)
  }, [])

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])
  let { mutateAsync: deleteFavorite, isPending: isDeletingFavorite } = useDeleteFavoriteMutation()
  let { mutateAsync: deleteWholesaleList, isPending: isDeletingWholesaleList } = useDeleteWholesaleListMutation()
  let currentLists = useMemo(() => lists?.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  ), [lists, currentPage])

  if (!lists) {
    return (
      <div className='flex justify-center items-center h-full'>
        <i className="animate-spin bx bx-loader-alt text-4xl text-primary"></i>
      </div>
    )
  }
  let pages = Math.ceil(lists.length / PAGE_SIZE)

  return <>
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Dateiname</th>
          <th>Produkte</th>
          <th>Markt</th>
          <th>Datum</th>
        </tr>
      </thead>
      <tbody>
        {currentLists.map((list) => {
          let asinCount = (list.total_favorites ?? list.asin_count) ?? 0
          let isScanComplete = list.available !== false
          return (
          <tr key={list.list_id}>
            <td className={styles.fileName}>
              {list.available || 'favorite_id' in list ?
                <a
                  href={`/wholesale/${list.favorite_id ? `favorite/${list.favorite_id}` : list.list_id}`}
                  className='font-bold inline-flex items-center gap-2'
                >
                  <span>{list.filename || list.favorite_name}</span>
                  <img src='/static/icons/export.svg' className='w-3 h-3 invert opacity-10' />
                </a> :
                <span className='text-gray-500'>
                  {list.filename}
                </span>
              }
            </td>
            <td>
              <div className='flex items-center gap-2 flex-wrap'>
                <span>{asinsLabel(asinCount)}</span>
                {asinCount === 0 && isScanComplete && (
                  <div
                    className='relative inline-flex items-center'
                    onMouseEnter={() => handleBadgeEnter(list.list_id)}
                    onMouseLeave={handleBadgeLeave}
                  >
                    <span className='text-[11px] text-amber-700 dark:text-amber-300 bg-amber-100/80 dark:bg-amber-400/10 border border-amber-200/80 dark:border-amber-300/30 rounded px-2 py-[2px]'>
                      Achtung
                    </span>
                    {hoveredListId === list.list_id && (
                      <div
                        className='flex absolute left-0 top-full mt-1 w-64 text-[11px] text-amber-900 dark:text-amber-100 bg-amber-50 dark:bg-neutral-900 border border-amber-200 dark:border-amber-400/40 rounded p-2 shadow-lg z-[60]'
                        onMouseEnter={() => handleBadgeEnter(list.list_id)}
                        onMouseLeave={handleBadgeLeave}
                      >
                        Stelle sicher, dass deine Liste Spalten enthält und du beim Hochladen alle Spalten korrekt zugeordnet hast.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </td>
            <td className={styles.markets}>
              <Flag country={(list.marketplace || list.region).toUpperCase()} />
            </td>
            <td>{list.timestamp}</td>
            <td className='whitespace-nowrap'>
              <div className='flex items-center gap-2'>
                {!list.favorite_id && !list.available && (
                  <div className='w-4 h-4 shrink-0'>
                    <ProgressCircle percentage={list.progress_percentage || 0} />
                  </div>
                )}
                <button
                  className='shrink-0'
                  disabled={isDeletingFavorite || isDeletingWholesaleList}
                  onClick={() => {
                    if (list.favorite_id) {
                      deleteFavorite(list.favorite_id)
                    } else {
                      deleteWholesaleList(list.list_id)
                    }
                  }}>
                  <img src='/static/icons/trash.svg' className='w-4 h-4 invert' />
                </button>
              </div>
            </td>
          </tr>
        )})}
      </tbody>
    </table>
    <div className={styles.pagination}>
      <div>Seite {currentPage} von {pages}</div>
      {currentPage > 1 && <button onClick={() => setCurrentPage(currentPage - 1)}>◀</button>}
      {currentPage < pages && <button onClick={() => setCurrentPage(currentPage + 1)}>▶</button>}
    </div>
  </>
}

function asinsLabel(count) {
  if (count === 1) {
    return `${count} Asin`
  } else if (count > 1) {
    return `${count} Asins`
  } else {
    return `0 Asins`;
  }
}
